import { useEffect, useRef, useState, useCallback } from "react";
import {
  MessagesSquare, Users, Share2, Send, Loader2, Copy, Check, Gift,
  Hash, Trophy, Mail, ChevronRight, Lock, Plus, X, Trash2, SmilePlus,
  UserPlus, UserCheck, UserMinus, Clock, CalendarPlus, Reply as ReplyIcon, CalendarIcon,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { zonedWallToUtc, DEFAULT_TZ } from "@/lib/timezones";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, InlineLoader, EmptyState } from "@/components/common";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const DURATIONS = [
  { m: 60, label: "1 hour" }, { m: 90, label: "1.5 hours" }, { m: 120, label: "2 hours" },
  { m: 180, label: "3 hours" }, { m: 240, label: "4 hours" },
];
const fmtSlot = (m) => { const h = Math.floor(m / 60), mm = m % 60; const h12 = ((h + 11) % 12) + 1; return `${h12}:${String(mm).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtHold = (iso) => {
  if (!iso) return null;
  const totalMin = Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60000));
  if (totalMin < 60) return `${totalMin}m`;
  const d = Math.floor(totalMin / 1440), h = Math.floor((totalMin % 1440) / 60), m = totalMin % 60;
  return d > 0 ? `${d}d ${h}h` : `${h}h ${m}m`;
};
// Build valid start-minute slots for a date given business hours + closures (Atlantic).
function useSchedule() {
  const [businessHours, setBusinessHours] = useState(null);
  const [closures, setClosures] = useState([]);
  useEffect(() => {
    apiClient.get("/business-hours").then(({ data }) => setBusinessHours(data)).catch(() => setBusinessHours([]));
    apiClient.get("/closures").then(({ data }) => setClosures(data)).catch(() => {});
  }, []);
  const slotsFor = useCallback((date, durationMin) => {
    if (!date) return { closed: false, closedLabel: null, slots: [] };
    const wd = date.getDay();
    const dh = (businessHours || []).find((d) => d.weekday === wd);
    const closure = closures.find((c) => c.date === dateKey(date));
    if (closure || dh?.is_closed) return { closed: true, closedLabel: closure ? `Closed — ${closure.name}` : "Closed this day", slots: [] };
    const openM = dh?.open_minutes ?? 540, closeM = dh?.close_minutes ?? 1320;
    const slots = [];
    for (let m = openM; m + durationMin <= closeM; m += 15) slots.push(m);
    return { closed: false, closedLabel: null, slots };
  }, [businessHours, closures]);
  return { slotsFor, ready: businessHours !== null };
}

const REACTIONS = ["👍", "🔥", "🏁", "👏", "🎉", "💪", "🙏", "🏆"];

const TABS = [
  { key: "chat", label: "Community Chat", icon: MessagesSquare },
  { key: "friends", label: "Friends", icon: Users },
  { key: "invite", label: "Invite Friends", icon: Share2 },
];

const roleColor = (role) => (role === "admin" ? "#ff2e97" : role === "staff" ? "#8b5cf6" : "#00e5ff");

export default function Community() {
  const [tab, setTab] = useState("chat");
  const [profileUser, setProfileUser] = useState(null);
  return (
    <div>
      <PageHeader title="Community" subtitle="Chat, squad up and grow the arena." />
      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`community-tab-${t.key}`}
            className="px-5 py-2.5 rounded-xl font-display font-semibold uppercase tracking-wide text-sm transition-all flex items-center gap-2"
            style={tab === t.key
              ? { background: "linear-gradient(90deg,#00e5ff,#8b5cf6)", color: "#05060d" }
              : { background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)", color: "#94a3b8" }}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "chat" && <ChatTab openProfile={setProfileUser} />}
      {tab === "friends" && <FriendsTab onSwitch={setTab} openProfile={setProfileUser} />}
      {tab === "invite" && <InviteTab />}
      {profileUser && <UserProfileModal userId={profileUser} onClose={() => setProfileUser(null)} />}
    </div>
  );
}

function ChatTab({ openProfile }) {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pickerFor, setPickerFor] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const endRef = useRef(null);

  const [myRequests, setMyRequests] = useState([]);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqForm, setReqForm] = useState({ name: "", reason: "" });
  const [reqSaving, setReqSaving] = useState(false);
  const [tentatives, setTentatives] = useState([]);
  const [showPropose, setShowPropose] = useState(false);
  const [convertT, setConvertT] = useState(null);

  const loadTentatives = useCallback((roomId) => {
    if (!roomId) return;
    apiClient.get(`/channels/${roomId}/tentative`).then(({ data }) => setTentatives(data)).catch(() => setTentatives([]));
  }, []);

  const loadRequests = useCallback(() => {
    apiClient.get("/chat/channel-requests/my").then(({ data }) => setMyRequests(data)).catch(() => {});
  }, []);

  useEffect(() => {
    apiClient.get("/chat/rooms").then(({ data }) => {
      setRooms(data);
      if (data.length) setActiveRoom(data[0]);
    }).catch(() => {}).finally(() => setLoadingRooms(false));
    loadRequests();
  }, [loadRequests]);

  const submitRequest = async () => {
    if (!reqForm.name.trim()) { toast.error("Channel name is required"); return; }
    setReqSaving(true);
    try {
      await apiClient.post("/chat/channel-requests", { name: reqForm.name.trim(), reason: reqForm.reason.trim() || null });
      toast.success("Request sent! An admin will review it.");
      setReqOpen(false); setReqForm({ name: "", reason: "" }); loadRequests();
    } catch (err) { toast.error(getErrorMessage(err, "Request failed")); } finally { setReqSaving(false); }
  };

  const pendingReq = myRequests.find((r) => r.status === "pending");

  const fetchMessages = useCallback(async (roomId, showLoader) => {
    if (showLoader) setLoadingMsgs(true);
    try {
      const { data } = await apiClient.get(`/chat/rooms/${roomId}/messages`, { params: { limit: 100 } });
      setMessages(data);
    } catch (e) { /* ignore */ } finally {
      if (showLoader) setLoadingMsgs(false);
    }
  }, []);

  useEffect(() => {
    if (!activeRoom) return;
    fetchMessages(activeRoom.id, true);
    loadTentatives(activeRoom.id);
    const id = setInterval(() => { fetchMessages(activeRoom.id, false); loadTentatives(activeRoom.id); }, 4000);
    return () => clearInterval(id);
  }, [activeRoom, fetchMessages, loadTentatives]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const msg = text.trim();
    if (!msg || !activeRoom) return;
    setSending(true);
    setText("");
    const rt = replyTo;
    setReplyTo(null);
    try {
      const { data } = await apiClient.post(`/chat/rooms/${activeRoom.id}/messages`, { message: msg, reply_to: rt?.id || null });
      setMessages((prev) => [...prev, data]);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to send"));
      setText(msg);
    } finally { setSending(false); }
  };

  const canModerate = user?.role === "admin" || user?.role === "staff";

  const react = async (m, emoji) => {
    setPickerFor(null);
    try {
      const { data } = await apiClient.post(`/chat/messages/${m.id}/react`, { emoji });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, reactions: data.reactions } : x)));
    } catch (err) { toast.error(getErrorMessage(err, "Reaction failed")); }
  };

  const deleteMessage = async (id) => {
    if (!id) return;
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    try {
      await apiClient.delete(`/admin/chat/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Message deleted");
    } catch (err) { toast.error(getErrorMessage(err, "Delete failed")); }
  };

  const joinTent = async (t) => {
    try {
      await apiClient.post(`/tentative/${t.id}/join`);
      loadTentatives(activeRoom.id);
    } catch (err) { toast.error(getErrorMessage(err, "Could not join")); }
  };
  const leaveTent = async (t) => {
    try {
      await apiClient.post(`/tentative/${t.id}/leave`);
      loadTentatives(activeRoom.id);
    } catch (err) { toast.error(getErrorMessage(err, "Could not leave")); }
  };
  const cancelTent = async (t) => {
    if (!window.confirm("Cancel this proposal?")) return;
    try {
      await apiClient.delete(`/tentative/${t.id}`);
      loadTentatives(activeRoom.id);
    } catch (err) { toast.error(getErrorMessage(err, "Could not cancel")); }
  };

  if (loadingRooms) return <InlineLoader label="Loading chat" />;

  return (
    <div className="grid lg:grid-cols-4 gap-5">
      {/* Rooms */}
      <div className="lg:col-span-1">
        <div className="glass rounded-2xl p-3">
          <p className="text-slate-400 text-xs uppercase tracking-widest px-2 py-2">Channels</p>
          <div className="flex lg:flex-col gap-1.5 overflow-x-auto scrollbar-thin">
            {rooms.map((r) => (
              <button key={r.id} onClick={() => setActiveRoom(r)} data-testid={`chat-room-${r.id}`}
                className="flex items-center gap-2 px-3 py-2.5 rounded-xl font-semibold transition-all whitespace-nowrap text-left"
                style={activeRoom?.id === r.id
                  ? { background: "rgba(0,229,255,0.12)", border: "1px solid rgba(0,229,255,0.45)", color: "#fff" }
                  : { border: "1px solid transparent", color: "#94a3b8" }}>
                {r.is_public === false ? <Lock className="w-4 h-4 shrink-0" style={{ color: "#ff2e97" }} /> : <Hash className="w-4 h-4 shrink-0" />} <span className="truncate">{r.name}</span>
              </button>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-white/10">
            {pendingReq ? (
              <p className="text-xs text-slate-400 px-2 flex items-center gap-1.5" data-testid="channel-request-pending"><Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: "#ffb020" }} /> {`"${pendingReq.name}" request pending review`}</p>
            ) : (
              <button onClick={() => setReqOpen(true)} data-testid="request-channel-btn"
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl font-semibold text-sm transition-all"
                style={{ border: "1px dashed rgba(255,46,151,0.4)", color: "#ff2e97" }}>
                <Plus className="w-4 h-4" /> Request Channel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="lg:col-span-3">
        <div className="glass rounded-2xl flex flex-col" style={{ height: "62vh", minHeight: 420 }}>
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <p className="font-display font-bold text-white">{activeRoom?.name || "Channel"}</p>
              {activeRoom?.description && <p className="text-slate-500 text-xs">{activeRoom.description}</p>}
            </div>
            <div className="flex items-center gap-2">
              {activeRoom && (
                <button onClick={() => setShowPropose(true)} className="neon-outline-btn px-3 py-1.5 text-xs flex items-center gap-2" data-testid="propose-session-btn" title="Propose a session to this channel">
                  <CalendarPlus className="w-3.5 h-3.5" /> Propose Session
                </button>
              )}
              {activeRoom && activeRoom.is_public === false && (
                <button
                  onClick={async () => {
                    try {
                      const { data } = await apiClient.post(`/chat/rooms/${activeRoom.id}/invites`, { expires_in_hours: 24 * 7 });
                      const url = `${window.location.origin}/invite/${data.code}`;
                      await navigator.clipboard.writeText(url);
                      toast.success("Invite link copied — share it anywhere");
                    } catch (err) { toast.error(getErrorMessage(err, "Could not create invite")); }
                  }}
                  className="neon-outline-btn px-3 py-1.5 text-xs flex items-center gap-2"
                  data-testid="channel-invite-btn"
                  title="Create invite link"
                >
                  <Share2 className="w-3.5 h-3.5" /> Invite
                </button>
              )}
            </div>
          </div>

          {tentatives.length > 0 && (
            <div className="px-5 py-3 border-b border-white/10 space-y-2 max-h-[30vh] overflow-y-auto scrollbar-thin" data-testid="tentative-bar">
              {tentatives.map((t) => (
                <div key={t.id} className="rounded-xl p-3" style={{ background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.3)" }} data-testid={`tentative-${t.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-sm flex items-center gap-1.5"><CalendarPlus className="w-4 h-4" style={{ color: "#c4b5fd" }} /> {t.proposed_date}{t.proposed_time ? ` @ ${t.proposed_time}` : ""}</p>
                      {t.note && <p className="text-xs text-slate-400 mt-0.5">{t.note}</p>}
                      <p className="text-[11px] text-slate-500 mt-1">{t.joiners.length} in{t.joiner_profiles?.length ? ` · ${t.joiner_profiles.map((j) => j.name).join(", ")}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {t.is_organizer ? (
                        <>
                          <button onClick={() => setConvertT(t)} className="neon-btn px-3 py-1.5 text-sm" data-testid={`tentative-convert-${t.id}`}>Set up &amp; pay</button>
                          <button onClick={() => cancelTent(t)} className="text-slate-400 hover:text-pink-400" title="Cancel proposal" data-testid={`tentative-cancel-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                        </>
                      ) : t.joined ? (
                        <button onClick={() => leaveTent(t)} className="neon-outline-btn px-3 py-1.5 text-sm" data-testid={`tentative-leave-${t.id}`}><Check className="w-4 h-4" style={{ color: "#00ff9d" }} /> Joined</button>
                      ) : (
                        <button onClick={() => joinTent(t)} className="neon-btn px-3 py-1.5 text-sm" data-testid={`tentative-join-${t.id}`}><UserPlus className="w-4 h-4" /> Join</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}


          <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-4">
            {loadingMsgs ? (
              <InlineLoader label="Loading messages" />
            ) : messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <MessagesSquare className="w-10 h-10 text-slate-600 mb-3" />
                <p className="text-slate-400">No messages yet. Be the first to say GG!</p>
              </div>
            ) : messages.map((m, i) => {
              const mine = m.user_id === user?.id;
              const isSystem = m.type !== "message" || m.user_id === "system";
              if (isSystem) {
                return (
                  <div key={m.id || i} className="text-center group">
                    <span className="chip" style={{ background: "rgba(139,92,246,0.14)", border: "1px solid rgba(139,92,246,0.4)", color: "#c4b5fd" }}>
                      {m.message}
                    </span>
                    {m.id && (
                      <button onClick={() => setReplyTo({ id: m.id, username: m.username || "proposal", message: m.message })}
                        className="ml-2 text-slate-500 hover:text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity align-middle"
                        title="Reply / suggest a change" data-testid={`chat-reply-${m.id}`}>
                        <ReplyIcon className="w-3.5 h-3.5 inline" />
                      </button>
                    )}
                  </div>
                );
              }
              return (
                <div key={m.id || i} className={`group flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${i}`}>
                  <div className="max-w-[75%]">
                    <div className={`flex items-center gap-2 mb-1 ${mine ? "justify-end" : ""}`}>
                      {mine ? (
                        <span className="text-xs font-bold" style={{ color: roleColor(m.role) }}>You</span>
                      ) : (
                        <button onClick={() => m.user_id && m.user_id !== "system" && openProfile(m.user_id)}
                          className="text-xs font-bold hover:underline" style={{ color: roleColor(m.role) }}
                          data-testid={`chat-user-${m.user_id}`}>{m.username}</button>
                      )}
                      <span className="text-[10px] text-slate-600">{m.timestamp ? new Date(m.timestamp).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                      {canModerate && m.id && (
                        <button onClick={() => deleteMessage(m.id)} data-testid={`chat-delete-${m.id}`}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-pink-400"
                          title="Delete message (admin)">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {m.id && (
                        <button onClick={() => setReplyTo({ id: m.id, username: m.username || "message", message: m.message })} data-testid={`chat-reply-${m.id}`}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-cyan-400" title="Reply / quote">
                          <ReplyIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {m.reply_snippet && (
                      <div className={`mb-1 px-2.5 py-1 rounded-lg text-xs border-l-2 ${mine ? "ml-auto" : ""}`} style={{ background: "rgba(255,255,255,0.04)", borderColor: "#8b5cf6", color: "#a9b4cc", maxWidth: "100%" }} data-testid={`chat-quote-${m.id}`}>
                        <span className="font-bold" style={{ color: "#c4b5fd" }}>{m.reply_snippet.username}</span>: {m.reply_snippet.message}
                      </div>
                    )}
                    <div className="rounded-2xl px-4 py-2.5 text-sm"
                      style={mine
                        ? { background: "linear-gradient(90deg, rgba(0,229,255,0.22), rgba(139,92,246,0.22))", border: "1px solid rgba(0,229,255,0.35)", color: "#eaf2ff" }
                        : { background: "rgba(8,11,22,0.7)", border: "1px solid rgba(120,150,220,0.14)", color: "#dbe4f5" }}>
                      {m.message}
                    </div>
                    <div className={`flex flex-wrap items-center gap-1 mt-1 ${mine ? "justify-end" : ""}`}>
                      {Object.entries(m.reactions || {}).map(([emoji, users]) => {
                        const mineR = (users || []).includes(user?.id);
                        return (
                          <button key={emoji} onClick={() => react(m, emoji)} data-testid={`reaction-${m.id}-${emoji}`}
                            className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1 transition-transform hover:scale-105"
                            style={mineR
                              ? { background: "rgba(0,229,255,0.18)", border: "1px solid rgba(0,229,255,0.5)" }
                              : { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
                            <span>{emoji}</span><span className="text-slate-300">{(users || []).length}</span>
                          </button>
                        );
                      })}
                      <div className="relative">
                        <button onClick={() => setPickerFor(pickerFor === m.id ? null : m.id)} data-testid={`react-add-${m.id}`}
                          className="text-slate-500 hover:text-cyan-400 transition-opacity opacity-60 group-hover:opacity-100">
                          <SmilePlus className="w-4 h-4" />
                        </button>
                        {pickerFor === m.id && (
                          <div className={`absolute z-30 bottom-full mb-1 flex gap-1 p-1.5 rounded-xl glass ${mine ? "right-0" : "left-0"}`}
                            style={{ border: "1px solid rgba(120,150,220,0.25)" }} data-testid={`react-picker-${m.id}`}>
                            {REACTIONS.map((e) => (
                              <button key={e} onClick={() => react(m, e)} data-testid={`react-pick-${m.id}-${e}`}
                                className="text-lg hover:scale-125 transition-transform">{e}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>

          <div className="p-4 border-t border-white/10">
            {replyTo && (
              <div className="flex items-center justify-between gap-2 mb-2 px-3 py-1.5 rounded-lg text-xs" style={{ background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.3)" }} data-testid="reply-bar">
                <span className="truncate text-slate-300"><ReplyIcon className="w-3.5 h-3.5 inline mr-1" style={{ color: "#c4b5fd" }} />Replying to <b style={{ color: "#c4b5fd" }}>{replyTo.username}</b>: {replyTo.message?.slice(0, 60)}</span>
                <button onClick={() => setReplyTo(null)} className="text-slate-400 hover:text-white shrink-0" data-testid="reply-cancel"><X className="w-4 h-4" /></button>
              </div>
            )}
            <div className="flex items-center gap-3">
              <input className="input-cyber" placeholder={replyTo ? `Reply to ${replyTo.username}…` : `Message ${activeRoom?.name || ""}`} value={text} maxLength={500}
                onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                data-testid="chat-input" />
              <button onClick={send} disabled={sending || !text.trim()} className="neon-btn px-5 py-3 shrink-0" data-testid="chat-send">
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {reqOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="request-channel-modal">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setReqOpen(false)} />
          <div className="relative w-full max-w-md glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-bold text-white flex items-center gap-2"><Lock className="w-5 h-5" style={{ color: "#ff2e97" }} /> Request a Channel</h3>
              <button onClick={() => setReqOpen(false)} className="text-slate-400 hover:text-white"><X className="w-6 h-6" /></button>
            </div>
            <p className="text-slate-400 text-sm mb-4">Request a private channel (e.g. for tournament planning). An admin will review and set it up for you.</p>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Channel Name</label>
            <input className="input-cyber mb-4" placeholder="e.g. Weekend GT3 Squad" value={reqForm.name} onChange={(e) => setReqForm({ ...reqForm, name: e.target.value })} data-testid="request-channel-name" />
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Reason (optional)</label>
            <textarea className="input-cyber min-h-[70px] resize-none mb-4" placeholder="What's this channel for?" value={reqForm.reason} onChange={(e) => setReqForm({ ...reqForm, reason: e.target.value })} data-testid="request-channel-reason" />
            <button onClick={submitRequest} disabled={reqSaving} className="neon-btn w-full py-3" data-testid="request-channel-submit">
              {reqSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send Request</>}
            </button>
          </div>
        </div>
      )}

      {showPropose && activeRoom && (
        <ProposeSessionModal roomId={activeRoom.id} onClose={() => setShowPropose(false)} onDone={() => { setShowPropose(false); loadTentatives(activeRoom.id); }} />
      )}
      {convertT && (
        <ConvertTentativeModal tentative={convertT} onClose={() => setConvertT(null)} onDone={() => { setConvertT(null); loadTentatives(activeRoom.id); }} />
      )}
    </div>
  );
}

function FriendAvatar({ f, size = 44 }) {
  return f.avatar_url ? (
    <img src={f.avatar_url} alt="" className="rounded-full object-cover" style={{ width: size, height: size }} />
  ) : (
    <div className="rounded-full flex items-center justify-center font-bold text-white" style={{ width: size, height: size, background: "linear-gradient(135deg,#00e5ff,#8b5cf6)" }}>
      {(f.name || "?").slice(0, 1).toUpperCase()}
    </div>
  );
}

function FriendsTab({ onSwitch, openProfile }) {
  const { user } = useAuth();
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [groups, setGroups] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([apiClient.get("/friends"), apiClient.get("/friends/requests"), apiClient.get("/group-bookings/mine")])
      .then(([fr, rq, gb]) => { setFriends(fr.data); setRequests(rq.data); setGroups(gb.data); })
      .catch(() => toast.error("Failed to load friends"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const respond = async (userId, accept) => {
    try {
      await apiClient.post("/friends/respond", { user_id: userId, accept });
      toast.success(accept ? "Friend added!" : "Request declined");
      load();
    } catch (err) { toast.error(getErrorMessage(err, "Action failed")); }
  };
  const removeFriend = async (userId) => {
    try { await apiClient.delete(`/friends/${userId}`); toast.success("Friend removed"); load(); } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };

  if (loading) return <InlineLoader label="Loading friends" />;

  const empty = friends.length === 0 && requests.incoming.length === 0 && requests.outgoing.length === 0;

  return (
    <div className="space-y-6">
      <GroupBookingsSection groups={groups} me={user} onReload={load} onCreate={() => setShowCreate(true)} friendsCount={friends.length} />
      {requests.incoming.length > 0 && (
        <SectionCard>
          <h3 className="font-display font-bold text-white mb-3 flex items-center gap-2"><UserPlus className="w-4 h-4" style={{ color: "#00e5ff" }} /> Friend Requests</h3>
          <div className="space-y-2">
            {requests.incoming.map((f) => (
              <div key={f.id} className="flex items-center gap-3 p-2 rounded-xl" style={{ background: "rgba(8,11,22,0.5)" }} data-testid={`friend-request-${f.id}`}>
                <button onClick={() => openProfile(f.id)}><FriendAvatar f={f} /></button>
                <button onClick={() => openProfile(f.id)} className="flex-1 text-left text-white font-semibold hover:underline">{f.name}</button>
                <button onClick={() => respond(f.id, true)} className="neon-btn px-3 py-1.5 text-sm" data-testid={`friend-accept-${f.id}`}><UserCheck className="w-4 h-4" /> Accept</button>
                <button onClick={() => respond(f.id, false)} className="neon-outline-btn px-3 py-1.5 text-sm" data-testid={`friend-decline-${f.id}`}><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
      <SectionCard>
        <h3 className="font-display font-bold text-white mb-3 flex items-center gap-2"><Users className="w-4 h-4" style={{ color: "#00ff9d" }} /> My Friends ({friends.length})</h3>
        {empty ? (
          <EmptyState icon={Users} title="No friends yet" subtitle="Tap a name in chat to view their profile and add them — or come to a group session and everyone who checks in together gets connected automatically." />
        ) : friends.length === 0 ? (
          <p className="text-slate-400 text-sm">No accepted friends yet. Check your requests above.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2">
            {friends.map((f) => (
              <div key={f.id} className="flex items-center gap-3 p-2 rounded-xl group" style={{ background: "rgba(8,11,22,0.5)" }} data-testid={`friend-${f.id}`}>
                <button onClick={() => openProfile(f.id)}><FriendAvatar f={f} /></button>
                <button onClick={() => openProfile(f.id)} className="flex-1 text-left text-white font-semibold hover:underline">{f.name}</button>
                <button onClick={() => removeFriend(f.id)} className="text-slate-400 hover:text-pink-400 transition-colors sm:opacity-0 sm:group-hover:opacity-100" title="Remove friend" data-testid={`friend-remove-${f.id}`}><UserMinus className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
        {requests.outgoing.length > 0 && (
          <div className="mt-4 pt-3 border-t border-white/10">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Pending sent</p>
            {requests.outgoing.map((f) => (
              <div key={f.id} className="flex items-center gap-2 text-slate-400 text-sm py-1"><Clock className="w-3.5 h-3.5" /> {f.name}</div>
            ))}
          </div>
        )}
      </SectionCard>
      {showCreate && <GroupBookingCreateModal friends={friends} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function GroupBookingsSection({ groups, me, onReload, onCreate, friendsCount }) {
  const [busyId, setBusyId] = useState(null);
  const respond = async (gid, accept) => {
    try { await apiClient.post(`/group-bookings/${gid}/respond`, { accept }); onReload(); } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };
  const pay = async (gid) => {
    setBusyId(gid);
    try { const { data } = await apiClient.post(`/group-bookings/${gid}/pay?client_origin=${encodeURIComponent(window.location.origin)}`); window.location.href = data.url; }
    catch (err) { toast.error(getErrorMessage(err, "Payment failed")); setBusyId(null); }
  };
  const cover = async (gid) => {
    setBusyId(gid);
    try { const { data } = await apiClient.post(`/group-bookings/${gid}/cover-balance?client_origin=${encodeURIComponent(window.location.origin)}`); window.location.href = data.url; }
    catch (err) { toast.error(getErrorMessage(err, "Failed")); setBusyId(null); }
  };
  const STATUS_LABEL = { pending_responses: "Waiting on replies", collecting_payment: "Collecting payment", confirmed: "Confirmed" };
  return (
    <SectionCard>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-white flex items-center gap-2"><CalendarPlus className="w-4 h-4" style={{ color: "#00e5ff" }} /> Session Invites</h3>
        <button onClick={onCreate} disabled={friendsCount === 0} className="neon-btn px-4 py-2 text-sm disabled:opacity-40" data-testid="group-create-open" title={friendsCount === 0 ? "Add friends first" : ""}><Plus className="w-4 h-4" /> Book with friends</button>
      </div>
      {groups.length === 0 ? (
        <p className="text-slate-400 text-sm">No session invites yet. Propose a date to your friends and split the cost — or let one person cover it.</p>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => {
            const mine = g.participants.find((p) => p.user_id === me?.id);
            const isOrg = g.organizer_id === me?.id;
            const unpaid = g.participants.filter((p) => p.status === "accepted" && !p.paid);
            const when = g.start_time ? new Date(g.start_time).toLocaleString() : "TBD";
            return (
              <div key={g.id} className="rounded-xl p-3" style={{ background: "rgba(8,11,22,0.5)", border: "1px solid rgba(120,150,220,0.14)" }} data-testid={`group-booking-${g.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-white font-semibold text-sm">{when}</p>
                    <p className="text-xs text-slate-400">{STATUS_LABEL[g.status] || g.status} · {g.pay_mode === "split" ? "Split evenly" : "Organizer pays"} · ${g.total_amount}</p>
                    {g.status !== "confirmed" && g.expires_at && (
                      <p className="text-xs mt-0.5 inline-flex items-center gap-1" style={{ color: "#ffb020" }} data-testid={`group-hold-${g.id}`}>
                        <Clock className="w-3 h-3" /> Hold expires in {fmtHold(g.expires_at)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {mine?.status === "invited" && (
                      <>
                        <button onClick={() => respond(g.id, true)} className="neon-btn px-3 py-1.5 text-sm" data-testid={`group-accept-${g.id}`}><Check className="w-4 h-4" /> Accept</button>
                        <button onClick={() => respond(g.id, false)} className="neon-outline-btn px-3 py-1.5 text-sm" data-testid={`group-decline-${g.id}`}><X className="w-4 h-4" /></button>
                      </>
                    )}
                    {g.status !== "confirmed" && mine?.status === "accepted" && !mine?.paid && (g.pay_mode === "split" || isOrg) && (
                      <button onClick={() => pay(g.id)} disabled={busyId === g.id} className="neon-btn px-3 py-1.5 text-sm disabled:opacity-60" data-testid={`group-pay-${g.id}`}>{busyId === g.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : `Pay $${mine.share}`}</button>
                    )}
                    {g.status === "confirmed" && <span className="chip" style={{ background: "rgba(0,255,157,0.12)", border: "1px solid rgba(0,255,157,0.4)", color: "#00ff9d" }}><Check className="w-3 h-3" /> Confirmed</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {g.participants.map((p) => (
                    <span key={p.user_id} className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: "rgba(255,255,255,0.05)", color: p.status === "declined" ? "#94a3b8" : "#dbe4f5" }}>
                      {p.paid ? <Check className="w-3 h-3" style={{ color: "#00ff9d" }} /> : p.status === "declined" ? <X className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {(p.profile?.name || "Racer")}{p.user_id === g.organizer_id ? " (host)" : ""}
                    </span>
                  ))}
                </div>
                {isOrg && g.status === "collecting_payment" && unpaid.some((p) => p.user_id !== me?.id) && (
                  <button onClick={() => cover(g.id)} disabled={busyId === g.id} className="neon-outline-btn w-full py-2 mt-2 text-sm disabled:opacity-60" data-testid={`group-cover-${g.id}`}>{busyId === g.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : `Cover the balance for everyone ($${unpaid.reduce((s, p) => s + (p.paid ? 0 : p.share), 0).toFixed(2)})`}</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

function GroupBookingCreateModal({ friends, onClose, onCreated }) {
  const [stations, setStations] = useState([]);
  const [form, setForm] = useState({ date: "", time: "18:00", hours: 1, stationIds: [], payMode: "split", friendIds: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => { apiClient.get("/stations").then(({ data }) => setStations(data)).catch(() => {}); }, []);

  const toggle = (key, id) => setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));

  const submit = async () => {
    if (!form.date || form.stationIds.length === 0 || form.friendIds.length === 0) { toast.error("Pick a date, at least one station and a friend"); return; }
    setBusy(true);
    let createdBookingId = null;
    try {
      const start = new Date(`${form.date}T${form.time}:00`);
      const end = new Date(start.getTime() + form.hours * 3600000);
      const { data: booking } = await apiClient.post("/bookings", {
        station_ids: form.stationIds, start_time: start.toISOString(), end_time: end.toISOString(), party_size: form.friendIds.length + 1,
      });
      createdBookingId = booking.id;
      await apiClient.post("/group-bookings", { booking_id: booking.id, invitee_ids: form.friendIds, pay_mode: form.payMode, source: "friends" });
      toast.success("Invites sent!");
      onCreated();
    } catch (err) {
      if (createdBookingId) { apiClient.post(`/bookings/${createdBookingId}/cancel`).catch(() => {}); }
      toast.error(getErrorMessage(err, "Could not create invite"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="group-create-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg glass rounded-2xl p-6 max-h-[90vh] overflow-y-auto scrollbar-thin">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold text-white">Book with Friends</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-6 h-6" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><label className="text-xs text-slate-400">Date</label><input type="date" className="input-cyber" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="group-date" /></div>
          <div><label className="text-xs text-slate-400">Start</label><input type="time" className="input-cyber" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="group-time" /></div>
        </div>
        <div className="mb-3"><label className="text-xs text-slate-400">Duration</label>
          <select className="input-cyber" value={form.hours} onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })}>
            {[1, 2, 3, 4].map((h) => <option key={h} value={h} className="bg-slate-900">{h} hour{h > 1 ? "s" : ""}</option>)}
          </select>
        </div>
        <div className="mb-3">
          <label className="text-xs text-slate-400">Stations</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {stations.map((s) => (
              <button key={s.id} onClick={() => toggle("stationIds", s.id)} data-testid={`group-station-${s.id}`}
                className="px-3 py-1.5 rounded-lg text-sm" style={form.stationIds.includes(s.id) ? { background: "rgba(0,229,255,0.16)", border: "1px solid rgba(0,229,255,0.5)", color: "#fff" } : { border: "1px solid rgba(120,150,220,0.16)", color: "#94a3b8" }}>{s.name}</button>
            ))}
          </div>
        </div>
        <div className="mb-3">
          <label className="text-xs text-slate-400">Invite friends</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {friends.map((f) => (
              <button key={f.id} onClick={() => toggle("friendIds", f.id)} data-testid={`group-friend-${f.id}`}
                className="px-3 py-1.5 rounded-lg text-sm" style={form.friendIds.includes(f.id) ? { background: "rgba(139,92,246,0.18)", border: "1px solid rgba(139,92,246,0.5)", color: "#fff" } : { border: "1px solid rgba(120,150,220,0.16)", color: "#94a3b8" }}>{f.name}</button>
            ))}
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs text-slate-400">Who pays?</label>
          <div className="flex gap-2 mt-1">
            <button onClick={() => setForm({ ...form, payMode: "split" })} className="flex-1 py-2 rounded-lg text-sm font-semibold" style={form.payMode === "split" ? { background: "rgba(0,229,255,0.16)", border: "1px solid rgba(0,229,255,0.5)", color: "#fff" } : { border: "1px solid rgba(120,150,220,0.16)", color: "#94a3b8" }} data-testid="group-paymode-split">Split evenly</button>
            <button onClick={() => setForm({ ...form, payMode: "organizer" })} className="flex-1 py-2 rounded-lg text-sm font-semibold" style={form.payMode === "organizer" ? { background: "rgba(0,229,255,0.16)", border: "1px solid rgba(0,229,255,0.5)", color: "#fff" } : { border: "1px solid rgba(120,150,220,0.16)", color: "#94a3b8" }} data-testid="group-paymode-organizer">I&apos;ll pay for all</button>
          </div>
        </div>
        <button onClick={submit} disabled={busy} className="neon-btn w-full py-3" data-testid="group-create-submit">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Send invites</>}
        </button>
      </div>
    </div>
  );
}

function UserProfileModal({ userId, onClose }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState("none");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([apiClient.get(`/users/${userId}/public`), apiClient.get(`/friends/status/${userId}`)])
      .then(([p, s]) => { setProfile(p.data); setStatus(s.data.status); })
      .catch(() => toast.error("Could not load profile"))
      .finally(() => setLoading(false));
  }, [userId]);

  const act = async (fn) => { setBusy(true); try { await fn(); } catch (err) { toast.error(getErrorMessage(err, "Action failed")); } finally { setBusy(false); } };
  const sendRequest = () => act(async () => { const { data } = await apiClient.post("/friends/request", { user_id: userId }); setStatus(data.status); toast.success(data.status === "friends" ? "Friend added!" : "Request sent"); });
  const respond = (accept) => act(async () => { const { data } = await apiClient.post("/friends/respond", { user_id: userId, accept }); setStatus(data.status); toast.success(accept ? "Friend added!" : "Declined"); });
  const remove = () => act(async () => { await apiClient.delete(`/friends/${userId}`); setStatus("none"); });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="user-profile-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm glass rounded-2xl p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="profile-modal-close"><X className="w-6 h-6" /></button>
        {loading ? <InlineLoader label="Loading" /> : !profile ? <p className="text-slate-400">Profile unavailable.</p> : (
          <div className="flex flex-col items-center text-center">
            <FriendAvatar f={{ name: profile.display_name || profile.username, avatar_url: profile.avatar_url }} size={72} />
            <h3 className="font-display text-xl font-bold text-white mt-3">{profile.display_name || profile.username || "Racer"}</h3>
            {profile.tier && <span className="chip mt-1" style={{ background: "rgba(0,229,255,0.12)", border: "1px solid rgba(0,229,255,0.3)", color: "#00e5ff" }}>{String(profile.tier).toUpperCase()}</span>}
            {profile.bio && <p className="text-slate-400 text-sm mt-2">{profile.bio}</p>}
            {profile.badge_count > 0 && <p className="text-slate-400 text-sm mt-2">🏅 {profile.badge_count} badge{profile.badge_count === 1 ? "" : "s"} unlocked</p>}
            <div className="w-full mt-5">
              {userId === user?.id ? (
                <p className="text-slate-500 text-sm">This is you.</p>
              ) : status === "friends" ? (
                <button onClick={remove} disabled={busy} className="neon-outline-btn w-full py-2.5" data-testid="profile-remove-friend"><UserCheck className="w-4 h-4" style={{ color: "#00ff9d" }} /> Friends · Remove</button>
              ) : status === "outgoing" ? (
                <button disabled className="neon-outline-btn w-full py-2.5 opacity-70"><Clock className="w-4 h-4" /> Request sent</button>
              ) : status === "incoming" ? (
                <div className="flex gap-2">
                  <button onClick={() => respond(true)} disabled={busy} className="neon-btn flex-1 py-2.5" data-testid="profile-accept-friend"><UserCheck className="w-4 h-4" /> Accept</button>
                  <button onClick={() => respond(false)} disabled={busy} className="neon-outline-btn flex-1 py-2.5"><X className="w-4 h-4" /> Decline</button>
                </div>
              ) : (
                <button onClick={sendRequest} disabled={busy} className="neon-btn w-full py-2.5" data-testid="profile-add-friend">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add Friend</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProposeSessionModal({ roomId, onClose, onDone }) {
  const { slotsFor } = useSchedule();
  const [date, setDate] = useState(null);
  const [startMin, setStartMin] = useState(null);
  const [durationMin, setDurationMin] = useState(60);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const { closed, closedLabel, slots } = slotsFor(date, durationMin);

  useEffect(() => { if (date && slots.length && (startMin == null || !slots.includes(startMin))) setStartMin(slots[0]); }, [date, durationMin, slots, startMin]);

  const submit = async () => {
    if (!date) { toast.error("Pick a date"); return; }
    if (closed || startMin == null) { toast.error("Pick an open time slot"); return; }
    setBusy(true);
    try {
      await apiClient.post(`/channels/${roomId}/tentative`, {
        proposed_date: dateKey(date), proposed_time: `${String(Math.floor(startMin / 60)).padStart(2, "0")}:${String(startMin % 60).padStart(2, "0")}`,
        proposed_duration_minutes: durationMin, note: note.trim(),
      });
      toast.success("Proposal posted to the channel!");
      onDone();
    } catch (err) { toast.error(getErrorMessage(err, "Could not propose")); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="propose-session-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md glass rounded-2xl p-6 max-h-[90vh] overflow-y-auto scrollbar-thin">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold text-white flex items-center gap-2"><CalendarPlus className="w-5 h-5" style={{ color: "#c4b5fd" }} /> Propose a Session</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-6 h-6" /></button>
        </div>
        <p className="text-slate-400 text-sm mb-4">Suggest a time to the channel — only open hours are selectable. Members tap Join, then you turn it into a real booking.</p>
        <label className="text-xs text-slate-400">Date</label>
        <Popover>
          <PopoverTrigger asChild>
            <button className="input-cyber flex items-center justify-between w-full mb-3" data-testid="propose-date">
              {date ? format(date, "EEEE, MMM d, yyyy") : "Pick a date"} <CalendarIcon className="w-4 h-4 text-slate-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 glass border-white/10" align="start">
            <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
          </PopoverContent>
        </Popover>
        <div className="mb-3">
          <label className="text-xs text-slate-400">Duration</label>
          <select className="input-cyber" value={durationMin} onChange={(e) => setDurationMin(Number(e.target.value))} data-testid="propose-duration">
            {DURATIONS.map((d) => <option key={d.m} value={d.m} className="bg-slate-900">{d.label}</option>)}
          </select>
        </div>
        <div className="mb-3">
          <label className="text-xs text-slate-400">Start time</label>
          {!date ? <p className="text-slate-500 text-sm py-2">Pick a date first.</p>
            : closed ? <p className="text-pink-400 text-sm py-2" data-testid="propose-closed">{closedLabel} — pick another date.</p>
            : slots.length === 0 ? <p className="text-pink-400 text-sm py-2">No open slots for this duration.</p>
            : <select className="input-cyber" value={startMin ?? ""} onChange={(e) => setStartMin(Number(e.target.value))} data-testid="propose-time">
                {slots.map((m) => <option key={m} value={m} className="bg-slate-900">{fmtSlot(m)}</option>)}
              </select>}
        </div>
        <label className="text-xs text-slate-400">Note (optional)</label>
        <input className="input-cyber mb-4" placeholder="e.g. GT3 endurance night" value={note} maxLength={140} onChange={(e) => setNote(e.target.value)} data-testid="propose-note" />
        <button onClick={submit} disabled={busy || !date || closed || startMin == null} className="neon-btn w-full py-3 disabled:opacity-50" data-testid="propose-submit">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Posting...</> : <>Post proposal</>}
        </button>
      </div>
    </div>
  );
}

function ConvertTentativeModal({ tentative, onClose, onDone }) {
  const { slotsFor } = useSchedule();
  const [stations, setStations] = useState([]);
  const [avail, setAvail] = useState(null); // set of available station ids, null = loading
  const origDate = tentative.proposed_date, origTime = tentative.proposed_time || "18:00";
  const origDur = tentative.proposed_duration_minutes || 60;
  const [date, setDate] = useState(new Date(`${origDate}T12:00:00`));
  const [startMin, setStartMin] = useState(() => { const [h, m] = origTime.split(":").map(Number); return h * 60 + (m || 0); });
  const [durationMin, setDurationMin] = useState(origDur);
  const [payMode, setPayMode] = useState("split");
  const [stationIds, setStationIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const { closed, closedLabel, slots } = slotsFor(date, durationMin);

  useEffect(() => { apiClient.get("/stations").then(({ data }) => setStations(data)).catch(() => {}); }, []);
  useEffect(() => { if (slots.length && !slots.includes(startMin)) setStartMin(slots[0]); }, [slots, startMin]);

  const startISO = date ? zonedWallToUtc(date.getFullYear(), date.getMonth(), date.getDate(), startMin, DEFAULT_TZ).toISOString() : null;
  const endISO = startISO ? new Date(new Date(startISO).getTime() + durationMin * 60000).toISOString() : null;

  useEffect(() => {
    if (!startISO || !endISO || closed) { setAvail(new Set()); return; }
    setAvail(null);
    let active = true;
    apiClient.get("/stations/availability", { params: { start_time: startISO, end_time: endISO } })
      .then(({ data }) => { if (active) { const ids = new Set(data.map((s) => s.id)); setAvail(ids); setStationIds((prev) => prev.filter((id) => ids.has(id))); } })
      .catch(() => active && setAvail(new Set()));
    return () => { active = false; };
  }, [startISO, endISO, closed]);

  const dateChanged = dateKey(date) !== origDate;
  const timeChanged = dateChanged || `${String(Math.floor(startMin / 60)).padStart(2, "0")}:${String(startMin % 60).padStart(2, "0")}` !== origTime || durationMin !== origDur;
  const toggle = (id) => { if (avail && !avail.has(id)) return; setStationIds((f) => f.includes(id) ? f.filter((x) => x !== id) : [...f, id]); };

  const submit = async () => {
    if (closed) { toast.error("That date is closed — pick another."); return; }
    if (stationIds.length === 0) { toast.error("Pick at least one available station"); return; }
    setBusy(true);
    let createdBookingId = null;
    try {
      const start = zonedWallToUtc(date.getFullYear(), date.getMonth(), date.getDate(), startMin, DEFAULT_TZ);
      const end = new Date(start.getTime() + durationMin * 60000);
      const { data: booking } = await apiClient.post("/bookings", {
        station_ids: stationIds, start_time: start.toISOString(), end_time: end.toISOString(), party_size: (tentative.joiners || []).length,
      });
      createdBookingId = booking.id;
      const invitees = (tentative.joiners || []).filter((u) => u !== tentative.organizer_id);
      const { data: gb } = await apiClient.post("/group-bookings", {
        booking_id: booking.id, invitee_ids: invitees, pay_mode: payMode, source: "chat", channel_id: tentative.room_id, pre_accept: !timeChanged,
      });
      await apiClient.post(`/tentative/${tentative.id}/convert`, {
        group_booking_id: gb.id, time_changed: timeChanged,
        proposed_date: dateKey(date), proposed_time: `${String(Math.floor(startMin / 60)).padStart(2, "0")}:${String(startMin % 60).padStart(2, "0")}`,
        proposed_duration_minutes: durationMin,
      });
      toast.success(timeChanged ? "Time updated — joiners must re-accept" : "Group booking created!", {
        description: timeChanged
          ? "Joiners re-accept the new time, then everyone pays in Friends → Session Invites."
          : (payMode === "split"
              ? `Cost is split evenly across ${(tentative.joiners || []).length} racer(s). Open Friends → Session Invites to pay your share.`
              : "You're covering the full cost. Open Friends → Session Invites to pay and lock it in."),
        duration: 10000,
      });
      onDone();
    } catch (err) {
      if (createdBookingId) { apiClient.post(`/bookings/${createdBookingId}/cancel`).catch(() => {}); }
      toast.error(getErrorMessage(err, "Could not set up the booking"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="convert-tentative-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg glass rounded-2xl p-6 max-h-[90vh] overflow-y-auto scrollbar-thin">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold text-white">Set up the session</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-6 h-6" /></button>
        </div>
        <p className="text-slate-400 text-sm mb-4">{(tentative.joiners || []).length} racer(s) joined. Availability is live — booked stations are greyed out. Changing the time means joiners re-accept before paying.</p>
        <label className="text-xs text-slate-400">Date</label>
        <Popover>
          <PopoverTrigger asChild>
            <button className="input-cyber flex items-center justify-between w-full mb-3" data-testid="convert-date">
              {format(date, "EEEE, MMM d, yyyy")} <CalendarIcon className="w-4 h-4 text-slate-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 glass border-white/10" align="start">
            <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
          </PopoverContent>
        </Popover>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><label className="text-xs text-slate-400">Duration</label>
            <select className="input-cyber" value={durationMin} onChange={(e) => setDurationMin(Number(e.target.value))} data-testid="convert-duration">
              {DURATIONS.map((d) => <option key={d.m} value={d.m} className="bg-slate-900">{d.label}</option>)}
            </select>
          </div>
          <div><label className="text-xs text-slate-400">Start</label>
            {closed ? <p className="text-pink-400 text-xs py-2" data-testid="convert-closed">{closedLabel}</p>
              : <select className="input-cyber" value={startMin} onChange={(e) => setStartMin(Number(e.target.value))} data-testid="convert-time">
                  {slots.map((m) => <option key={m} value={m} className="bg-slate-900">{fmtSlot(m)}</option>)}
                </select>}
          </div>
        </div>
        {timeChanged && !closed && <p className="text-xs mb-3" style={{ color: "#ffb020" }} data-testid="convert-changed-note">⚠ Time changed from the proposal — joiners will be asked to re-accept the new time.</p>}
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <label className="text-xs text-slate-400">Stations {avail === null && <Loader2 className="w-3 h-3 animate-spin inline ml-1" />}</label>
          </div>
          <div className="flex flex-wrap gap-2 mt-1">
            {stations.map((s) => {
              const booked = avail && !avail.has(s.id);
              const on = stationIds.includes(s.id);
              return (
                <button key={s.id} onClick={() => toggle(s.id)} disabled={booked} data-testid={`convert-station-${s.id}`}
                  className="px-3 py-1.5 rounded-lg text-sm relative disabled:cursor-not-allowed"
                  style={booked ? { border: "1px solid rgba(120,150,220,0.1)", color: "#5b6478", background: "rgba(8,11,22,0.4)", opacity: 0.55 }
                    : on ? { background: "rgba(0,229,255,0.16)", border: "1px solid rgba(0,229,255,0.5)", color: "#fff" }
                    : { border: "1px solid rgba(120,150,220,0.16)", color: "#94a3b8" }}>
                  {s.name}{booked && <span className="ml-1.5 text-[10px] uppercase" style={{ color: "#ff2e97" }}>booked</span>}
                </button>
              );
            })}
          </div>
          {avail && stations.length > 0 && avail.size === 0 && !closed && <p className="text-xs text-pink-400 mt-2" data-testid="convert-none-available">All stations are booked at this time — try another time or date.</p>}
        </div>
        <div className="mb-4">
          <label className="text-xs text-slate-400">Who pays?</label>
          <div className="flex gap-2 mt-1">
            <button onClick={() => setPayMode("split")} className="flex-1 py-2 rounded-lg text-sm font-semibold" style={payMode === "split" ? { background: "rgba(0,229,255,0.16)", border: "1px solid rgba(0,229,255,0.5)", color: "#fff" } : { border: "1px solid rgba(120,150,220,0.16)", color: "#94a3b8" }} data-testid="convert-paymode-split">Split evenly</button>
            <button onClick={() => setPayMode("organizer")} className="flex-1 py-2 rounded-lg text-sm font-semibold" style={payMode === "organizer" ? { background: "rgba(0,229,255,0.16)", border: "1px solid rgba(0,229,255,0.5)", color: "#fff" } : { border: "1px solid rgba(120,150,220,0.16)", color: "#94a3b8" }} data-testid="convert-paymode-organizer">I&apos;ll pay for all</button>
          </div>
          <p className="text-xs text-slate-500 mt-1.5" data-testid="convert-paymode-hint">
            {payMode === "split"
              ? `The total will be split evenly across ${(tentative.joiners || []).length} racer(s) — everyone pays their own share.`
              : "You'll pay the full cost; joiners just confirm their seat."}
          </p>
        </div>
        <button onClick={submit} disabled={busy || closed || stationIds.length === 0} className="neon-btn w-full py-3 disabled:opacity-50" data-testid="convert-submit">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Setting up...</> : <>Create booking</>}
        </button>
      </div>
    </div>
  );
}

function InviteTab() {
  const [copied, setCopied] = useState(false);
  const inviteLink = typeof window !== "undefined" ? window.location.origin : "";
  const shareText = `Join me at Pit Lane Lounge — elite gaming & esports! Sign up and get 1,000 welcome points: ${inviteLink}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success("Invite link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { toast.error("Could not copy link"); }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Join Pit Lane Lounge!", text: shareText, url: inviteLink }); }
      catch (e) { /* cancelled */ }
    } else { copy(); }
  };

  const socials = [
    { name: "WhatsApp", color: "#25D366", href: `https://wa.me/?text=${encodeURIComponent(shareText)}` },
    { name: "X / Twitter", color: "#1DA1F2", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}` },
    { name: "Facebook", color: "#1877F2", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(inviteLink)}` },
    { name: "Email", color: "#EA4335", href: `mailto:?subject=${encodeURIComponent("Join Pit Lane Lounge!")}&body=${encodeURIComponent(shareText)}` },
  ];

  const steps = ["Share your invite link", "Your friend signs up using it", "You both get rewarded with points"];

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <SectionCard style={{ border: "1px solid rgba(255,176,32,0.4)" }}>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,176,32,0.15)", border: "1px solid rgba(255,176,32,0.4)" }}>
              <Gift className="w-7 h-7" style={{ color: "#ffb020" }} />
            </div>
            <div>
              <h3 className="font-display text-2xl font-bold text-white">Earn 1,000 Points</h3>
              <p className="text-slate-300">for every friend who joins the arena</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard>
          <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Your Invite Link</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input className="input-cyber flex-1" readOnly value={inviteLink} data-testid="invite-link" />
            <button onClick={copy} className="neon-outline-btn px-5 py-3 shrink-0" data-testid="invite-copy">
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
            </button>
          </div>
          <button onClick={nativeShare} className="neon-btn w-full py-3.5 mt-4" data-testid="invite-share-now">
            <Share2 className="w-5 h-5" /> Share Now
          </button>
          <p className="text-center text-slate-500 text-xs my-4 uppercase tracking-widest">Or share via</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {socials.map((s) => (
              <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`invite-${s.name.split(" ")[0].toLowerCase()}`}
                className="glass glass-hover rounded-xl p-4 flex flex-col items-center gap-2 text-center">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${s.color}22`, border: `1px solid ${s.color}66` }}>
                  {s.name === "Email" ? <Mail className="w-5 h-5" style={{ color: s.color }} /> : <Share2 className="w-5 h-5" style={{ color: s.color }} />}
                </div>
                <span className="text-slate-300 text-xs font-semibold">{s.name}</span>
              </a>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="lg:col-span-1">
        <SectionCard>
          <h3 className="font-display font-bold text-white mb-5 flex items-center gap-2"><Trophy className="w-5 h-5" style={{ color: "#ffb020" }} /> How It Works</h3>
          <div className="space-y-5">
            {steps.map((s, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-full flex items-center justify-center font-display font-bold text-black shrink-0" style={{ background: "linear-gradient(135deg,#00e5ff,#8b5cf6)" }}>{i + 1}</span>
                <p className="text-slate-300 pt-1">{s}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-2 text-slate-400 text-sm">
            <ChevronRight className="w-4 h-4" style={{ color: "#00ff9d" }} /> Rewards apply once your friend completes sign up.
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
